import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Virtual, Mousewheel, Keyboard, EffectCreative } from "swiper/modules";
import "../../../styles/components/Slices/FeatureTurner.scss";
import SlicesVideoComponent from "../SlicesVideoComponent";
import useWindowWidth from "../../../hooks/useBetterMediaQuery";

const HeroTurnerSlider = ({ primary, carouselData }) => {
  const sliderRef = useRef();
  const [activeTab, setActiveTab] = useState(2);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const isTablet = useWindowWidth(520);
  const [isPaused, setIsPaused] = useState(false);
  const wasClickedRef = useRef(false);

  const tabs = carouselData
    .map((card) => card?.tab_title?.text || card?.cell_title?.text)
    .filter((title) => title && title.trim() !== "");

  const pauseProgress = () => {
    clearInterval(intervalRef.current);
    setIsPaused(true);
  };

  const startProgress = () => {
    clearInterval(intervalRef.current);

    const increment = wasClickedRef.current ? 1.6 : 3.2; // Slower if clicked

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(intervalRef.current);
          wasClickedRef.current = false; // Reset click flag after it's done

          if (isTablet) {
            handleProgressBarEnd();
          } else {
            goToNextTab();
          }
          return 0;
        }
        return prev + increment;
      });
    }, 100);
  };

  useEffect(() => {
    if (!isPaused) {
      startProgress();
    } else {
      clearInterval(intervalRef.current); // prevent animation from starting
    }
    return () => clearInterval(intervalRef.current);
  }, [activeTab]);

  useEffect(() => {
    if (sliderRef.current && sliderRef.current.swiper) {
      sliderRef.current.swiper.slideToLoop(2);
    }
  }, []);

  const goToNextTab = () => {
    if (sliderRef.current && sliderRef.current.swiper) {
      sliderRef.current.swiper.slideNext(); // Only this
      setProgress(0);
    }
  };

  const goToPreviousTab = () => {
    if (sliderRef.current && sliderRef.current.swiper) {
      sliderRef.current.swiper.slidePrev(); // Only this
      setProgress(0);
    }
  };

  const handleProgressBarEnd = () => {
    const nextIndex = activeTab + 1 < tabs?.length ? activeTab + 1 : 0;
    if (sliderRef.current?.swiper) {
      sliderRef.current.swiper.slideNext();
    }
    setActiveTab(nextIndex);
    setProgress(0);
  };

  const handleTabClick = (index) => {
    if (sliderRef.current && sliderRef.current.swiper) {
      sliderRef.current.swiper.slideToLoop(index);
      setActiveTab(index);
      setProgress(0);
      wasClickedRef.current = true;

      if (!isPaused) {
        startProgress();
      } else {
        clearInterval(intervalRef.current);
      }
    }
  };

  const getStartIndex = (activeTab, totalTabs) => {
    if (activeTab <= 0) return 0;
    if (activeTab >= totalTabs - 1) return Math.max(totalTabs - 3, 0);
    return activeTab - 1;
  };

  const tabItems = tabs.map((title, index) => ({ title, index }));

  return (
    <div
      className={`flex flex-col-reverse sm:flex-col hero-turner-slider-bg overflow-visible lg:flex-col`}
    >
      <div className="flex flex-col lg:flex-row w-full justify-center">
        <div
          className="flex sm:gap-2 sm:mx-auto my-2 sm:max-w-fit flex-row sm:px-2 ease-in-out transition-all duration-300 justify-between w-full rounded-lg bg-[#EDEEF0] lg:flex-row "
          onMouseEnter={() => {
            if (!isTablet) {
              pauseProgress();
            }
          }}
          onMouseLeave={() => {
            if (!isTablet) {
              setIsPaused(false);
              startProgress();
            }
          }}
        >
          {!isTablet ? (
            <>
              <button
                aria-label="prev-btn"
                className="relative flex px-4 py-3.5 gap-2 my-1 items-center sm:hidden rounded-lg bg-white"
                onClick={goToPreviousTab}
              >
                {/* Left Arrow SVG */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={13}
                  height={14}
                  fill="none"
                >
                  <path
                    fill="#434343"
                    d="M12.86 6.19H2.575L8.73.845A.14.14 0 0 0 8.639.6H7.084a.282.282 0 0 0-.185.068L.256 6.432a.562.562 0 0 0 0 .849l6.682 5.8a.139.139 0 0 0 .091.034h1.609a.14.14 0 0 0 .091-.246L2.575 7.525H12.86c.077 0 .14-.063.14-.14V6.33a.141.141 0 0 0-.14-.14Z"
                  />
                </svg>
              </button>

              <div className="flex justify-center items-center max-w-full overflow-hidden">
                {tabItems[activeTab] && (
                  <button
                    onClick={() => handleTabClick(activeTab)}
                    className={`relative flex px-4 py-2 gap-2 my-1 items-center rounded-lg cursor-pointer bg-[#F1F1F1] text-purple-1100 active_tab_box_shadow`}
                  >
                    <span className="absolute inset-0 rounded-[4px] z-0 overflow-hidden">
                      <span
                        className="absolute top-0 left-0 h-full transition-all duration-300 ease-in-out bg-gradient-to-r from-white via-white to-[#f0f0f0]"
                        style={{ width: `${progress}%` }}
                      />
                    </span>
                    <p className="relative z-10 font-manrope text-lg font-medium -tracking-[0.9px] text-left">
                      {tabItems[activeTab].title}
                    </p>
                  </button>
                )}
              </div>

              <button
                aria-label="forward-btn"
                className="relative flex px-4 py-3.5 gap-2 my-1 items-center sm:hidden rounded-lg bg-white"
                onClick={goToNextTab}
              >
                {/* Right Arrow SVG */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={13}
                  height={14}
                  fill="none"
                >
                  <path
                    fill="#434343"
                    d="M.14 7.525h10.285L4.27 12.87a.14.14 0 0 0 .092.246h1.555a.282.282 0 0 0 .185-.068l6.643-5.764a.563.563 0 0 0 0-.85L6.062.636A.138.138 0 0 0 5.971.6H4.362a.14.14 0 0 0-.091.246l6.154 5.343H.14A.141.141 0 0 0 0 6.33v1.055c0 .077.063.14.14.14Z"
                  />
                </svg>
              </button>
            </>
          ) : (
            tabs.map((title, index) => (
              <button
                key={index}
                onClick={() => handleTabClick(index)}
                className={`relative flex min-w-fit px-2.5 py-1 my-1 gap-2 items-center rounded-[4px] cursor-pointer border-[0.75px] border-transparent
                ${index === activeTab ? " text-purple-1100 active_tab_box_shadow bg-[#F1F1F1]" : "hover:bg-[#ffffffbf] hover:border-[0.75px] hover:border-[#d8d9dc4d] text-gray-2500"}`}
              >
                {/* Circular Progress Border */}
                {index === activeTab && (
                  <span className="absolute inset-0 rounded-[4px] z-0 overflow-hidden">
                    <span
                      className="absolute top-0 left-0 h-full transition-all duration-300 ease-in-out bg-gradient-to-r from-white via-white to-[#f0f0f0]"
                      style={{
                        width: `${progress}%`,
                      }}
                    />
                  </span>
                )}

                <p className="relative z-10 font-manrope text-xl font-medium tracking-tight_1 text-left">
                  {title}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      <Swiper
        direction="horizontal"
        keyboard={{ enabled: true, onlyInViewport: true }}
        mousewheel={{ forceToAxis: true }}
        forceToAxis={true}
        modules={[Virtual, Mousewheel, Keyboard, EffectCreative]}
        ref={sliderRef}
        slidesPerView={1}
        loop={true}
        speed={1000}
        onSlideChange={(swiper) => {
          setIsPaused(false);
          setActiveTab(swiper.realIndex);
          setProgress(0);
          wasClickedRef.current = false;
        }}
        className="slider mySwiper3"
      >
        {carouselData
          ?.filter((cardData) => cardData?.cell_image?.url)
          ?.map((cardData, key) => (
            <SwiperSlide virtualIndex={key} key={key} style={{ width: "100%" }}>
              {cardData?.cell_video?.url ? (
                <SlicesVideoComponent
                  loop
                  autoPlay
                  muted
                  className="w-full h-auto mx-auto rounded-md lg:max-w-6xl lg:px-4"
                  src={cardData?.cell_video?.url}
                />
              ) : (
                <img
                  src={cardData?.cell_image?.url}
                  alt={cardData?.cell_image?.alt || "cell_image"}
                  loading="lazy"
                  className="w-full h-auto mx-auto rounded-md lg:max-w-6xl lg:px-0"
                />
              )}
            </SwiperSlide>
          ))}
      </Swiper>
    </div>
  );
};

export default HeroTurnerSlider;
